import { CATEGORIES, COLORS, SUBCATEGORIES } from '../constants';
import { compareActivityDesc } from '../itemDates';
import { S } from '../strings';
import type { Category, ParsedItem } from '../types';
import { UpdateCard } from './UpdateCard';

export interface SingleCategoryViewProps {
  category: Category;
  subcategory: string | null;
  onSelectSubcategory: (sub: string | null) => void;
  // Category-scoped (respects category only) — drives the subcategory pill
  // counts so "Azure AI Services 2" reflects the true in-category count even
  // when a type/search filter further narrows the rendered cards below.
  catItems: ParsedItem[];
  // Fully filtered (category + subcategory + type + search + bookmarks-only)
  // — this is what the card grid renders.
  items: ParsedItem[];
  isBookmarked: (id: string) => boolean;
  onToggleBookmark: (item: ParsedItem) => void;
  isMuted?: (id: string) => boolean;
  onToggleMute?: (item: ParsedItem) => void;
}

export function SingleCategoryView({
  category,
  subcategory,
  onSelectSubcategory,
  catItems,
  items,
  isBookmarked,
  onToggleBookmark,
  isMuted,
  onToggleMute,
}: SingleCategoryViewProps) {
  const catColor = COLORS.category[category];
  const label = CATEGORIES.find((c) => c.value === category)?.label ?? category;
  const subs = SUBCATEGORIES[category] ?? [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 18, height: 18, borderRadius: 5, background: catColor }} />
        <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>
          {label}
        </span>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)' }}>
          {items.length} päivitystä
        </span>
      </div>

      {subs.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, flexWrap: 'wrap' }}>
          <button
            className="pill"
            onClick={() => onSelectSubcategory(null)}
            style={{
              padding: '5px 14px',
              borderRadius: 16,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              border: '1.5px solid',
              transition: 'all 0.2s',
              borderColor: !subcategory ? catColor : 'var(--border)',
              background: !subcategory ? catColor + '18' : 'var(--card-bg)',
              color: !subcategory ? catColor : 'var(--text-muted)',
            }}
          >
            {S.allCategory(catItems.length)}
          </button>
          {subs.map((sub) => {
            const count = catItems.filter((i) => i.subcategory === sub.value).length;
            if (count === 0) return null;
            const active = subcategory === sub.value;
            return (
              <button
                key={sub.value}
                className="pill"
                onClick={() => onSelectSubcategory(active ? null : sub.value)}
                style={{
                  padding: '5px 14px',
                  borderRadius: 16,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  transition: 'all 0.2s',
                  borderColor: active ? catColor : 'var(--border)',
                  background: active ? catColor + '18' : 'var(--card-bg)',
                  color: active ? catColor : 'var(--text-muted)',
                }}
              >
                {sub.label} {count}
              </button>
            );
          })}
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
          gap: 12,
        }}
      >
        {items
          .slice()
          .sort(compareActivityDesc)
          .map((item, i) => (
            <UpdateCard
              key={item.id}
              item={item}
              style={{ animationDelay: `${i * 0.02}s` }}
              bookmarked={isBookmarked(item.id)}
              onToggleBookmark={() => onToggleBookmark(item)}
              muted={isMuted?.(item.id)}
              onToggleMute={onToggleMute}
            />
          ))}
      </div>
      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          {S.emptyCategory}
        </div>
      )}
    </div>
  );
}
